import Joi from 'joi';
import RecurringPattern from 'models/recurringPattern';
import { getLocalizedMessageOptions } from 'services/Localization/joiValidation';
import { newGuidNil } from 'utils/guid';
import { isObjectEqual } from 'utils/object';
import { AuditType, InternalAuditUser } from 'models/internalAudit';
import { areNormIdsEqual } from 'models/auditCriteria';

// The audit series: what is audited, by whom and how often. Operational occurrences
// (InternalAudit) are spawned from the template by its recurring pattern, which also
// carries the series start date (the Integration model): without an active pattern the
// series has no scheduled occurrences, only ad hoc ones.
export default class InternalAuditTemplate {
  tenantId: string;

  internalAuditTemplateId: number;

  name: string;

  description?: string;

  auditType: AuditType;

  ownerId?: string;

  ownerRoleId?: string;

  authSchemaId?: number;

  recurrencePattern: RecurringPattern;

  // Optional event template whose subtasks are the procedure library for the execution phase
  taskTemplateId?: number;

  // Server-owned: the series has a saved setup, so an occurrence can be reset to it. Cloned but
  // deliberately outside isEqual - it is never sent back, so it must not make the panel dirty.
  hasSetup: boolean;

  // Server-owned like hasSetup: the audit whose setup was last saved to this series, and when.
  // Cleared server-side when the series scope is edited by hand. Never sent back.
  setupSourceInternalAuditId?: number;

  setupSavedOn?: Date;

  auditTrailId: number;

  modified?: Date;

  users: InternalAuditUser[];

  // The standards (tenant norm ids) the series audits against; every occurrence copies them. At
  // least one is required to save. Undefined = unknown (a list read that did not load them): never
  // coerced to [], and not sent back, so the server leaves the stored set untouched.
  normIds: number[] | undefined;

  constructor() {
    this.tenantId = newGuidNil();
    this.internalAuditTemplateId = 0;
    this.name = '';
    this.auditType = AuditType.Internal;
    this.recurrencePattern = InternalAuditTemplate.getDefaultRecurrencePattern();
    this.hasSetup = false;
    this.auditTrailId = 0;
    this.users = [];
    this.normIds = undefined;
  }

  // Every 6 months starting today, but INACTIVE: planning a new series is optional, so a template
  // that is saved without touching the planning fields has no schedule (and no occurrences) yet.
  // Setting the start date or the recurrence in the UI activates it.
  static getDefaultRecurrencePattern(): RecurringPattern {
    const pattern = new RecurringPattern();
    pattern.initRecurringPatternMonth(6);
    pattern.isActive = false;

    return pattern;
  }

  isEqual(item: InternalAuditTemplate): boolean {
    if (item.internalAuditTemplateId !== this.internalAuditTemplateId) return false;
    if (item.name !== this.name) return false;
    if (item.description !== this.description) return false;
    if (item.auditType !== this.auditType) return false;
    if (item.ownerId !== this.ownerId) return false;
    if (item.ownerRoleId !== this.ownerRoleId) return false;
    if (item.authSchemaId !== this.authSchemaId) return false;
    if (item.taskTemplateId !== this.taskTemplateId) return false;
    if (!item.recurrencePattern.isEqual(this.recurrencePattern)) return false;
    if (
      !isObjectEqual(
        item.users.map((u) => u.userId + ':' + u.role),
        this.users.map((u) => u.userId + ':' + u.role),
      )
    )
      return false;
    if (!areNormIdsEqual(item.normIds, this.normIds)) return false;

    return true;
  }

  clone(): InternalAuditTemplate {
    const item = new InternalAuditTemplate();
    item.tenantId = this.tenantId;
    item.internalAuditTemplateId = this.internalAuditTemplateId;
    item.name = this.name;
    item.description = this.description;
    item.auditType = this.auditType;
    item.ownerId = this.ownerId;
    item.ownerRoleId = this.ownerRoleId;
    item.authSchemaId = this.authSchemaId;
    item.recurrencePattern = this.recurrencePattern.clone();
    item.taskTemplateId = this.taskTemplateId;
    item.hasSetup = this.hasSetup;
    item.setupSourceInternalAuditId = this.setupSourceInternalAuditId;
    item.setupSavedOn = this.setupSavedOn ? new Date(this.setupSavedOn) : undefined;
    item.auditTrailId = this.auditTrailId;
    item.modified = this.modified ? new Date(this.modified) : undefined;
    item.users = this.users.map((u) => u.clone());
    item.normIds = this.normIds ? [...this.normIds] : undefined;

    return item;
  }

  validate(localizedFields: Record<string, string>): Joi.ValidationResult {
    const schema: Joi.ObjectSchema = Joi.object({
      name: Joi.string().max(512).required().label(localizedFields['name']),
    }).prefs(getLocalizedMessageOptions());

    return schema.validate({ name: this.name }, { abortEarly: false });
  }
}

// One row of the annual-plan schedule: the series with the occurrences that touch the
// requested window and the future dates the recurring pattern predicts (server-computed).
export class InternalAuditSchedule {
  template: InternalAuditTemplate;

  occurrences: InternalAuditScheduleOccurrence[];

  calculatedDates: Date[];

  constructor() {
    this.template = new InternalAuditTemplate();
    this.occurrences = [];
    this.calculatedDates = [];
  }
}

export class InternalAuditScheduleOccurrence {
  internalAuditId: number;

  name: string;

  status: number;

  origin: number;

  startDate?: Date;

  endDate?: Date;

  scopeItemCount: number;

  constructor() {
    this.internalAuditId = 0;
    this.name = '';
    this.status = 0;
    this.origin = 0;
    this.scopeItemCount = 0;
  }
}
